import * as React from 'react';
const { useState, useCallback, useMemo, useRef, Fragment } = React;
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import Popper from '@material-ui/core/Popper';
import InputAdornment from '@material-ui/core/InputAdornment';
import Grow from '@material-ui/core/Grow';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import TextField, { TextFieldProps } from '../text-field';
import Box from '../box';
import IconButton from '../icon-button';
import SearchField from '../search-field';
import Icon from '../icon';
import { declOfNum } from '../../utils';
import {
	Root,
	TextFieldLayout,
	SearchFieldLayout,
	ItemsLayout,
	Placeholder,
	IconLayout,
} from './styled';


export type ItemsPickerProps<T> = {
	value: Array<T>;
	dataSource: Array<T>;
	getSourceText?: (item: T) => string;
	getSourceID?: (item: T) => string | number;
	itemsMsgs?: [string, string, string];
	noDataMsg?: React.ReactNode;
	followContentWidth?: boolean;
	renderItem?: (item: T) => React.ReactNode;
	filterFn?: (item: unknown, term: string) => boolean;
	searchbarPlaceholder?: string;
	onChange: (e: React.SyntheticEvent, value: Array<T>) => void;
} & Omit<TextFieldProps, 'value' | 'onChange'>;

export type ItemsPickerComponent<T = any> = React.SFC<ItemsPickerProps<T>>;

const ItemsPicker: ItemsPickerComponent = (props) => {
	const {
		value,
		fullWidth,
		getSourceText,
		getSourceID,
		dataSource,
		isFetching,
		itemsMsgs,
		noDataMsg,
		followContentWidth,
		renderItem,
		filterFn,
		searchbarPlaceholder,
		onChange,
		...rest
	} = props;
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const searchFieldLayoutRef = useRef<HTMLDivElement>(null);
	const processedValue = !value ? [] : value;
	const valueMap = useMemo(() => processedValue.reduce((acc, v) => (acc[getSourceID(v)] = v, acc), {}), [processedValue]);
	const textValue = useMemo(() => {
		return getTerm({
			value: processedValue,
			getSourceText,
			isFetching,
			itemsMsgs,
		});
	}, [processedValue, isFetching]);

	const handleClear = useCallback((e: React.SyntheticEvent) => {
		e.stopPropagation();
		onChange(e, []);
	}, []);
	const handleOpen = useCallback(() => {
		if (isFetching) return;
		setIsOpen(true);
		setTimeout(() => {
			const input = searchFieldLayoutRef.current.querySelector('input');
			input.focus();
		}, 100);

	}, [isFetching]);
	const handleClose = useCallback(() => setIsOpen(false), []);

	const handleToggleOpen = useCallback((e: React.SyntheticEvent) => {
		if (isFetching) return;
		e.stopPropagation();
		setIsOpen(toggle => !toggle);
	}, [isFetching]);

	const handleSearch = useCallback((e: React.SyntheticEvent, value: string) => {
		setSearchTerm(value);
	}, []);

	const handleSelectItem = useCallback((item: unknown) => (e: React.SyntheticEvent) => {
		const items = [...processedValue];
		const ID = getSourceID(item);

		if (!valueMap[ID]) {
			items.push(item);
		} else {
			const idx = processedValue.findIndex(x => getSourceID(x) === ID);

			if (idx !== -1) {
				items.splice(idx, 1);
			}
		}

		onChange(e, items);
	}, [valueMap, processedValue]);

	const renderContent = () => {
		const filteredDataSource = dataSource.filter(item => {
			const isMatch = typeof filterFn === 'function'
				? filterFn(item, searchTerm)
				: getSourceText(item).toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;

			return isMatch;
		});

		return (
			<Fragment>
				<SearchFieldLayout ref={searchFieldLayoutRef}>
					<SearchField
						value={searchTerm}
						placeholder={searchbarPlaceholder}
						fullWidth
						onChange={handleSearch}
					/>
				</SearchFieldLayout>
				<ItemsLayout>
					{
						filteredDataSource.length === 0
						?	<Placeholder>{noDataMsg}</Placeholder>
						: filteredDataSource.map((item, idx) => {
								const text = getSourceText(item);
								const isSelected = valueMap[getSourceID(item)];

								return (
									<MenuItem
										key={idx}
										component='div'
										style={{
											position: 'relative',
											minHeight: 24,
											whiteSpace: 'normal',
											lineHeight: 1.6,
											paddingLeft: 46,
										}}
										onClick={handleSelectItem(item)}>
										{
											isSelected &&
											<IconLayout>
												<Icon type='done' color='primary' />
											</IconLayout>
										}
										{
											typeof renderItem === 'function'
											?	renderItem(item)
											: text
										}
									</MenuItem>
							);
						})
					}
				</ItemsLayout>
			</Fragment>
		);
	};

	return (
		<Root fullWidth={fullWidth}>
			<TextFieldLayout onClick={handleOpen}>
				<TextField
					{...rest}
					value={textValue}
					fullWidth
					InputProps={{
						endAdornment: (
							<InputAdornment position='end' style={{position: 'absolute', right: 0}}>
								<Box display='inline-flex' alignItems='center'>
									<IconButton onClick={handleClear}>
										<Icon type='clear' width={14} height={14} />
									</IconButton>
									<Icon
										type='arrow-drop-down'
										color='muted'
										width={21}
										height={21}
										onClick={handleToggleOpen}
									/>
								</Box>
							</InputAdornment>
						),
						inputRef: (node: HTMLInputElement | null) => {
							setAnchorEl(node);
						},
					}}
				/>
				<Popper
					anchorEl={anchorEl}
					open={isOpen}
					transition
					style={{ zIndex: 1300, marginLeft: 14 }}>
					{
						({ TransitionProps, placement }) => (
							<ClickAwayListener onClickAway={handleClose}>
								<Grow
									{...TransitionProps}
									style={{
										transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
									}}>
										<Paper
											square
											elevation={4}
											style={{
												width: followContentWidth
													? undefined
													: anchorEl ? anchorEl.clientWidth : undefined,
												maxWidth: '100%',
												paddingTop: 8,
												paddingBottom: 8,
												overflow: 'auto',
											}}>
											{renderContent()}
										</Paper>
									</Grow>
							</ClickAwayListener>
						)
					}
				</Popper>
			</TextFieldLayout>
		</Root>
	);
}

ItemsPicker.displayName = 'ItemsPicker';
ItemsPicker.defaultProps = {
	getSourceText: item => item.name,
	getSourceID: item => item.value,
	margin: 'bottom-small',
	itemsMsgs: ['элемент', 'элемента', 'элементов'],
	noDataMsg: 'Нет элементов',
	placeholder: 'Выберите элемент',
	searchbarPlaceholder: 'Введите первые символы',
};

type getTermOptions<T> = {
	value: Array<T>;
	getSourceText: (item: T) => string;
	isFetching: boolean;
	itemsMsgs: [string, string, string];
}

function getTerm<T = any>(options: getTermOptions<T>) {
	const {
		value,
		getSourceText,
		isFetching,
		itemsMsgs,
	} = options;
	if (isFetching) return '';
	const list = value;
	if (list.length === 0) return '';
	if (list.length === 1) return getSourceText(list[0]);

	return `Выбрано ${list.length} ${declOfNum(list.length, itemsMsgs)}`;
}

export default ItemsPicker;
